// src/pages/dashboards/admin/AdminUsers.jsx
// Searchable, filterable, paged user list. Row opens the detail modal.

import React, { useEffect, useState, useCallback } from "react";
import { listUsers, createUser } from "../../../api/admin";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { Card, Input, Select, Badge, Button, Loading, EmptyState, Modal, Field } from "../../../components/ui";
import { Search, Plus } from "../../../components/icons";
import { relative, titleCase, date } from "../../../lib/format";
import UserDetailModal from "./UserDetailModal";

const ROLES = ["", "user", "practitioner", "company", "admin"];
const TIER_TONE = { free: "gray", starter: "blue", pro: "green" };
const PAGE = 50;

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE, offset };
      if (q.trim()) params.q = q.trim();
      if (role) params.role = role;
      setRows(await listUsers(params));
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [q, role, offset]);

  // debounce search/filter changes
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-display font-bold">Users</h1>
        <Button onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5">
          <Plus size={16} /> New user
        </Button>
      </div>

      {/* filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-tb-muted" />
          <Input
            value={q}
            onChange={(e) => { setOffset(0); setQ(e.target.value); }}
            placeholder="Search name or email"
            className="pl-9"
          />
        </div>
        <Select value={role} onChange={(e) => { setOffset(0); setRole(e.target.value); }} className="sm:w-48">
          {ROLES.map((r) => <option key={r} value={r}>{r ? titleCase(r) : "All roles"}</option>)}
        </Select>
      </div>

      <Card>
        {loading ? (
          <Loading label="Loading users" />
        ) : rows.length === 0 ? (
          <EmptyState title="No users found" hint="Adjust your search or filter." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-tb-muted border-b border-tb-border dark:border-tb-d-border">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Email</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Tier</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Joined</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Last login</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => setOpenId(u.id)}
                    className="border-b border-tb-border/60 dark:border-tb-d-border/60 last:border-0
                               hover:bg-tb-bg-2 dark:hover:bg-tb-d-inset cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{u.name}</span>
                        {!u.is_active && <Badge tone="red">Disabled</Badge>}
                      </div>
                      <span className="md:hidden text-xs text-tb-muted">{u.email}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-tb-muted">{u.email}</td>
                    <td className="px-4 py-3">{titleCase(u.role)}</td>
                    <td className="px-4 py-3"><Badge tone={TIER_TONE[u.tier] || "gray"}>{titleCase(u.tier)}</Badge></td>
                    <td className="px-4 py-3 hidden lg:table-cell text-tb-muted">{date(u.created_at)}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-tb-muted">{relative(u.last_login_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* pagination */}
      <div className="flex items-center justify-between mt-4">
        <Button variant="ghost" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - PAGE))}>
          Previous
        </Button>
        <span className="text-sm text-tb-muted">
          {offset + 1}–{offset + rows.length}
        </span>
        <Button variant="ghost" disabled={rows.length < PAGE} onClick={() => setOffset((o) => o + PAGE)}>
          Next
        </Button>
      </div>

      {openId && (
        <UserDetailModal
          userId={openId}
          currentAdminId={me?.id}
          onClose={() => setOpenId(null)}
          onChanged={load}
        />
      )}

      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); setOffset(0); load(); }}
        />
      )}
    </div>
  );
}

const NEW_ROLES = ["user", "practitioner", "company", "admin"];
const NEW_TIERS = ["free", "starter", "pro"];

function CreateUserModal({ onClose, onCreated }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "user", tier: "free" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 8) {
      toast.error("Name, email and a password of at least 8 characters are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        role: form.role,
        tier: form.tier,
      };
      if (form.phone.trim()) payload.phone = form.phone.trim();
      await createUser(payload);
      toast.success("User created");
      onCreated();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title="New user" width="max-w-lg">
      <div className="space-y-4">
        <Field label="Name"><Input value={form.name} onChange={set("name")} placeholder="Full name" /></Field>
        <Field label="Email"><Input type="email" value={form.email} onChange={set("email")} placeholder="name@example.com" /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Phone (optional)"><Input value={form.phone} onChange={set("phone")} placeholder="082 123 4567" /></Field>
          <Field label="Temporary password"><Input type="text" value={form.password} onChange={set("password")} placeholder="Min 8 characters" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Role">
            <Select value={form.role} onChange={set("role")}>
              {NEW_ROLES.map((r) => <option key={r} value={r}>{titleCase(r)}</option>)}
            </Select>
          </Field>
          <Field label="Tier">
            <Select value={form.tier} onChange={set("tier")}>
              {NEW_TIERS.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
            </Select>
          </Field>
        </div>
        <p className="text-xs text-tb-muted">Share the temporary password with the user; they can change it after signing in.</p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>Create user</Button>
        </div>
      </div>
    </Modal>
  );
}